import {
  Button,
  Flex,
  Paper,
  TextInput,
  Text,
  Loader,
  NumberInput,
  Switch,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  displayErrorMessage,
  displayServerErrorMessage,
} from "../shared/ErrorMessage";
import {
  CreateActivityRequest,
  useCreateActivityMutation,
} from "../slices/api/ventsApi";
import { initialize, updateField } from "../slices/form/createActivity";
import { useEffect, useRef } from "react";
import { Activity } from "../slices/types";

interface Props {
  title: string;
  ventId: string;
  updateTime: string;
  onClose: () => void;
}
const CreateActivity = (props: Props) => {
  const { data } = useAppSelector(
    (state: RootState) => state.createActivityForm
  );
  const [createActivity] = useCreateActivityMutation();
  const nameRefDescription = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      initialize({
        limitPerEvent: 1,
        limitPerGuest: 1,
        hostManaged: false,
      } as Activity)
    );
  }, []);

  const handleClick = async () => {
    if (data === undefined) {
      console.error("Activity is undefined but should not be");
      return;
    }
    if (data.description == null) {
      displayErrorMessage("Description is required.");
      if (nameRefDescription.current) nameRefDescription.current.focus();
      return;
    }

    const request: CreateActivityRequest = {
      activity: {
        description: data.description,
        limitPerEvent: data.limitPerEvent!,
        limitPerGuest: data.limitPerGuest,
        hostManaged: data.hostManaged,
      },
      ventId: props.ventId,
      updateTime: props.updateTime,
    };

    try {
      await createActivity(request).unwrap();
      props.onClose();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (!data) {
    return <Loader color="blue" />;
  }

  return (
    <>
      <Paper>
        <Text size="lg">{props.title}</Text>
        <TextInput
          maxLength={50}
          required
          ref={nameRefDescription}
          mt="md"
          label="Description"
          value={data.description ?? ""}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "description",
                value: event.currentTarget.value,
              })
            );
          }}
        />
        <NumberInput
          min={0}
          mt="md"
          allowNegative={false}
          allowDecimal={false}
          maxLength={6}
          label="Limit per guest"
          value={data.limitPerGuest ?? 0}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "limitPerGuest",
                value: value === "" ? 0 : value,
              })
            );
          }}
        />
        <NumberInput
          min={0}
          mt="md"
          allowNegative={false}
          allowDecimal={false}
          maxLength={6}
          label="Limit per event"
          description="Set to blank for no limit, otherwise the value is a hard limit per event."
          value={data.limitPerEvent ?? 0}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "limitPerEvent",
                value: value === "" ? 0 : value,
              })
            );
          }}
        />
        <Switch
          label="Host managed (guests cannot update assignments for this activity)"
          mt="md"
          checked={data.hostManaged}
          onChange={(event) =>
            dispatch(
              updateField({
                field: "hostManaged",
                value: event.currentTarget.checked,
              })
            )
          }
        />
        <Flex justify="flex-end">
          <Button mt="md" mr="md" onClick={props.onClose}>
            Cancel
          </Button>
          <Button mt="md" onClick={handleClick}>
            Save
          </Button>
        </Flex>
      </Paper>
    </>
  );
};

export default CreateActivity;
